'use client'

import { forwardRef, ReactNode, useState, useRef, useEffect } from 'react'
import { 
  ChevronDown, 
  ChevronUp, 
  MoreHorizontal, 
  AlertTriangle, 
  Info, 
  CheckCircle2, 
  Clock,
  Star,
  Heart
} from 'lucide-react'
import { 
  borderRadiusV3, 
  spacingV3, 
  shadowsV3, 
  animationsV3, 
  glassV3,
  componentSpacing,
  componentShadows,
  typographyV3 
} from '@birhaus/tokens/v3'
import { cn } from '@birhaus/types/utils'

/**
 * BIRHAUS Card V3 - Radical Minimalist Design
 * 
 * Revolutionary card component with:
 * - Radical border radius (40px default)
 * - Generous padding (2x traditional)
 * - Glass morphism variants
 * - Smooth hover animations
 * - Progressive disclosure
 * - Spanish-first labeling
 * - Advanced interaction states
 * - Performance optimized
 */


export interface BirhausCardV3Action {
  id: string
  label: string
  labelEs?: string
  labelEn?: string
  icon?: ReactNode
  onClick: () => void
  variant?: 'primary' | 'secondary' | 'destructive' | 'ghost'
  disabled?: boolean
  loading?: boolean
}

export interface BirhausCardV3Props extends React.HTMLAttributes<HTMLDivElement> {
  // BIRHAUS Principle #7: Spanish-first labeling
  title?: string
  titleEs?: string
  titleEn?: string
  
  subtitle?: string
  subtitleEs?: string
  subtitleEn?: string
  
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  
  // Content organization
  header?: ReactNode
  footer?: ReactNode
  media?: ReactNode
  children?: ReactNode
  
  // V3 Design System - Appearance
  variant?: 'default' | 'elevated' | 'glass' | 'gradient' | 'minimal' | 'frosted'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  radius?: keyof typeof borderRadiusV3
  
  // V3 Interactive states
  hoverable?: boolean
  selectable?: boolean
  selected?: boolean
  onSelect?: () => void
  
  // V3 Visual effects
  glow?: boolean
  float?: boolean
  pulse?: boolean
  shimmer?: boolean
  
  // BIRHAUS Principle #3: One clear action
  primaryAction?: BirhausCardV3Action
  secondaryActions?: BirhausCardV3Action[]
  
  // BIRHAUS Principle #4: Progressive disclosure
  collapsible?: boolean
  defaultExpanded?: boolean
  expandedContent?: ReactNode
  
  // Status system
  status?: 'neutral' | 'success' | 'warning' | 'error' | 'info' | 'loading'
  statusMessage?: string
  statusMessageEs?: string
  statusMessageEn?: string
  
  // Enhanced metadata
  badge?: string | number
  badgeVariant?: 'neutral' | 'primary' | 'success' | 'warning' | 'error'
  timestamp?: Date
  author?: string
  category?: string
  tags?: string[]
  
  // Interaction features
  bookmarkable?: boolean
  bookmarked?: boolean
  onBookmark?: () => void
  likeable?: boolean
  liked?: boolean
  onLike?: () => void
  
  // Performance tracking
  trackPerformance?: boolean
  
  // Loading states
  loading?: boolean
  skeleton?: boolean
}

export const BirhausCardV3 = forwardRef<HTMLDivElement, BirhausCardV3Props>(({
  // Content (Spanish-first)
  title,
  titleEs,
  titleEn,
  subtitle,
  subtitleEs,
  subtitleEn,
  description,
  descriptionEs,
  descriptionEn,
  
  // Layout
  header,
  footer,
  media,
  children,
  
  // V3 appearance
  variant = 'default',
  size = 'md',
  radius = 'xl',
  
  // Interactive states
  hoverable = false,
  selectable = false,
  selected = false,
  onSelect,
  
  // V3 effects
  glow = false,
  float = false,
  pulse = false,
  shimmer = false,
  
  // Actions
  primaryAction,
  secondaryActions = [],
  
  // Progressive disclosure
  collapsible = false,
  defaultExpanded = true,
  expandedContent,
  
  // Status
  status = 'neutral',
  statusMessage,
  statusMessageEs,
  statusMessageEn,
  
  // Metadata
  badge,
  badgeVariant = 'neutral',
  timestamp,
  author,
  category,
  tags = [],
  
  // Interactions
  bookmarkable = false,
  bookmarked = false,
  onBookmark,
  likeable = false,
  liked = false,
  onLike,
  
  // Performance
  trackPerformance = false,
  
  // States
  loading = false,
  skeleton = false,
  
  // Standard props
  className,
  onClick,
  ...props
}, ref) => {
  
  // State management
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const [isHovered, setIsHovered] = useState(false)
  const [isPressed, setIsPressed] = useState(false)
  
  const cardRef = useRef<HTMLDivElement>(null)
  const performanceStartRef = useRef<number>()
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const resolveText = (spanish?: string, english?: string, fallback?: string) => {
    return spanish || english || fallback || ''
  }
  
  const displayTitle = resolveText(titleEs, titleEn, title)
  const displaySubtitle = resolveText(subtitleEs, subtitleEn, subtitle)
  const displayDescription = resolveText(descriptionEs, descriptionEn, description)
  const displayStatusMessage = resolveText(statusMessageEs, statusMessageEn, statusMessage)
  
  // V3 Size classes with generous padding
  const sizeClasses = {
    sm: `p-6`,  // 24px -> 48px
    md: `p-8`,  // 32px -> 64px
    lg: `p-10`, // 40px -> 80px
    xl: `p-12`, // 48px -> 96px
  }
  
  // V3 Variant classes with radical design
  const variantClasses = {
    default: `
      bg-white border border-gray-100
      ${hoverable ? 'hover:shadow-xl hover:border-gray-200 hover:-translate-y-1' : ''}
      shadow-lg
    `,
    
    elevated: `
      bg-white
      ${hoverable ? 'hover:shadow-2xl hover:-translate-y-2' : ''}
      shadow-xl
    `,
    
    glass: `
      bg-white/50 backdrop-blur-lg border border-white/30
      ${hoverable ? 'hover:bg-white/60 hover:shadow-xl hover:-translate-y-1' : ''}
      shadow-lg
    `,
    
    frosted: `
      bg-white/70 backdrop-blur-xl border border-white/40
      ${hoverable ? 'hover:bg-white/80 hover:shadow-2xl hover:-translate-y-1' : ''}
      shadow-xl
    `,
    
    gradient: `
      bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30
      border border-gray-100
      ${hoverable ? 'hover:shadow-xl hover:from-white hover:via-blue-50/50 hover:to-purple-50/50 hover:-translate-y-1' : ''}
      shadow-lg
    `,
    
    minimal: `
      bg-transparent
      ${hoverable ? 'hover:bg-gray-50/50' : ''}
    `,
  }
  
  const statusClasses = {
    neutral: '',
    success: 'border-green-200/50 bg-green-50/30',
    warning: 'border-yellow-200/50 bg-yellow-50/30',
    error: 'border-red-200/50 bg-red-50/30',
    info: 'border-blue-200/50 bg-blue-50/30',
    loading: 'border-blue-200/50 bg-blue-50/20 animate-pulse',
  }
  
  const statusIcons = {
    neutral: null,
    success: <CheckCircle2 className="h-5 w-5 text-green-600" />,
    warning: <AlertTriangle className="h-5 w-5 text-yellow-600" />,
    error: <AlertTriangle className="h-5 w-5 text-red-600" />,
    info: <Info className="h-5 w-5 text-blue-600" />,
    loading: <div className="h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />,
  }
  
  const badgeClasses = {
    neutral: 'bg-gray-100 text-gray-800',
    primary: 'bg-blue-100 text-blue-800',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
  }
  
  // Event handlers
  const handleMouseEnter = () => {
    setIsHovered(true)
    if (trackPerformance) {
      performanceStartRef.current = performance.now()
    }
  }
  
  const handleMouseLeave = () => {
    setIsHovered(false)
    if (trackPerformance && performanceStartRef.current) {
      const duration = performance.now() - performanceStartRef.current
      console.log(`[BIRHAUS v3] Card hover took ${duration.toFixed(2)}ms`)
    }
  }
  
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (selectable) {
      onSelect?.()
    }
    onClick?.(event)
  }
  
  const baseClasses = cn(
    // Base styles with v3 principles
    'relative transition-all duration-300 ease-out overflow-hidden',
    'transform-gpu', // GPU acceleration
    
    // Size with generous padding
    sizeClasses[size],
    
    // Border radius - radical curves
    `rounded-[${borderRadiusV3[radius]}]`,
    
    // Variant styling
    variantClasses[variant],
    
    // Status styling
    status !== 'neutral' && statusClasses[status],
    
    // Interactive states
    selectable && 'cursor-pointer',
    selected && 'ring-4 ring-blue-500/30 border-blue-300',
    isPressed && 'scale-[0.98]',
    
    // Visual effects
    glow && 'shadow-[0_0_40px_-10px_rgba(31,111,235,0.25)]',
    pulse && 'animate-pulse',
    shimmer && 'animate-shimmer',
    
    className
  )
  
  // Skeleton loading state
  if (skeleton || loading) {
    return (
      <div ref={ref} className={baseClasses} {...props}>
        <div className="animate-pulse space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-3">
              <div className="h-6 bg-gray-200 rounded-lg w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded-md w-1/2"></div>
            </div>
            <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
          </div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded-md"></div>
            <div className="h-4 bg-gray-200 rounded-md w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded-md w-4/6"></div>
          </div>
          <div className="flex justify-end gap-3">
            <div className="h-10 w-20 bg-gray-200 rounded-lg"></div>
            <div className="h-10 w-24 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div 
      ref={ref || cardRef} 
      className={baseClasses}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      data-birhaus-v3="card"
      data-variant={variant}
      data-size={size}
      {...props}
    >
      {/* Media section */}
      {media && (
        <div className={cn(
          `-m-${sizeClasses[size].split(' ')[0].replace('p-', '')}`,
          `mb-${spacingV3[6]}`,
          `rounded-t-[${borderRadiusV3[radius]}]`,
          'overflow-hidden'
        )}>
          {media}
        </div>
      )}
      
      {/* Custom header */}
      {header && (
        <div className={cn(
          'mb-6 pb-6 border-b border-gray-200/50'
        )}>
          {header}
        </div>
      )}
      
      {/* Card header with title, metadata, and actions */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1 min-w-0">
          {/* Badge */}
          {badge && (
            <div className="mb-3">
              <span className={cn(
                'inline-flex items-center rounded-full px-3 py-1 text-sm font-medium',
                badgeClasses[badgeVariant]
              )}>
                {badge}
              </span>
            </div>
          )}
          
          {/* Title */}
          {displayTitle && (
            <h3 className="text-2xl font-semibold text-gray-900 mb-2 leading-tight">
              {displayTitle}
            </h3>
          )}
          
          {/* Subtitle */}
          {displaySubtitle && (
            <p className="text-lg text-gray-600 mb-4 leading-relaxed">
              {displaySubtitle}
            </p>
          )}
          
          {/* Tags */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {tags.map((tag, index) => (
                <span
                  key={index}
                  className={cn(
                    'inline-flex items-center px-3 py-1 text-xs font-medium',
                    'bg-gray-100 text-gray-700 rounded-full',
                    'hover:bg-gray-200 transition-colors'
                  )}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
          
          {/* Metadata */}
          {(timestamp || author || category) && (
            <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
              {timestamp && (
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  <span>{timestamp.toLocaleDateString('es-ES', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</span>
                </div>
              )}
              {author && <span>por {author}</span>}
              {category && (
                <span className={cn(
                  'px-3 py-1 bg-gray-100 rounded-full text-xs font-medium'
                )}>
                  {category}
                </span>
              )}
            </div>
          )}
        </div>
        
        {/* Action buttons */}
        <div className="flex items-center gap-3 ml-6">
          {/* Interactive buttons */}
          {bookmarkable && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onBookmark?.()
              }}
              className={cn(
                'p-2 rounded-full transition-all duration-200',
                'hover:bg-gray-100 active:bg-gray-200',
                bookmarked ? 'text-blue-600 bg-blue-50' : 'text-gray-400'
              )}
              aria-label={bookmarked ? 'Quitar marcador' : 'Agregar marcador'}
            >
              <Star className={cn('h-5 w-5', bookmarked && 'fill-current')} />
            </button>
          )}
          
          {likeable && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onLike?.()
              }}
              className={cn(
                'p-2 rounded-full transition-all duration-200',
                'hover:bg-red-50 active:bg-red-100',
                liked ? 'text-red-500 bg-red-50' : 'text-gray-400'
              )}
              aria-label={liked ? 'Quitar me gusta' : 'Me gusta'}
            >
              <Heart className={cn('h-5 w-5', liked && 'fill-current')} />
            </button>
          )}
          
          {/* Primary action */}
          {primaryAction && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                primaryAction.onClick()
              }}
              disabled={primaryAction.disabled || primaryAction.loading}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium',
                `rounded-[${borderRadiusV3.md}]`,
                'transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-offset-2',
                'disabled:pointer-events-none disabled:opacity-50',
                primaryAction.variant === 'primary' && 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
                primaryAction.variant === 'destructive' && 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
                (!primaryAction.variant || primaryAction.variant === 'secondary') && 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500'
              )}
            >
              {primaryAction.loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
              ) : primaryAction.icon}
              {primaryAction.labelEs || primaryAction.label}
            </button>
          )}
          
          {/* Secondary actions menu */}
          {secondaryActions.length > 0 && (
            <button
              className={cn(
                'p-2 text-gray-400 hover:text-gray-600 rounded-full',
                'hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500'
              )}
              aria-label="Más acciones"
            >
              <MoreHorizontal className="h-5 w-5" />
            </button>
          )}
          
          {/* Collapsible toggle */}
          {collapsible && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setIsExpanded(!isExpanded)
              }}
              className={cn(
                'p-2 text-gray-400 hover:text-gray-600 rounded-full',
                'hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500',
                'transition-all duration-200'
              )}
              aria-label={isExpanded ? 'Contraer' : 'Expandir'}
            >
              {isExpanded ? (
                <ChevronUp className="h-5 w-5" />
              ) : (
                <ChevronDown className="h-5 w-5" />
              )}
            </button>
          )}
        </div>
      </div>
      
      {/* Status message */}
      {displayStatusMessage && (
        <div className={cn(
          'flex items-center gap-3 p-4 rounded-2xl mb-6 text-sm',
          'backdrop-blur-sm',
          status === 'success' && 'bg-green-100/50 text-green-800 border border-green-200/50',
          status === 'warning' && 'bg-yellow-100/50 text-yellow-800 border border-yellow-200/50',
          status === 'error' && 'bg-red-100/50 text-red-800 border border-red-200/50',
          status === 'info' && 'bg-blue-100/50 text-blue-800 border border-blue-200/50'
        )}>
          {statusIcons[status]}
          <span className="font-medium">{displayStatusMessage}</span>
        </div>
      )}
      
      {/* Description */}
      {displayDescription && (
        <p className="text-gray-600 text-base leading-relaxed mb-6">
          {displayDescription}
        </p>
      )}
      
      {/* Main content */}
      {children && (
        <div className="mb-6">
          {children}
        </div>
      )}
      
      {/* Expanded content with smooth animation */}
      {collapsible && expandedContent && (
        <div
          className={cn(
            'overflow-hidden transition-all duration-500 ease-out',
            isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
          )}
        >
          <div className="pt-6 border-t border-gray-200/50">
            {expandedContent}
          </div>
        </div>
      )}
      
      {/* Custom footer */}
      {footer && (
        <div className={cn(
          'mt-6 pt-6 border-t border-gray-200/50',
          'bg-gray-50/50 -mx-8 -mb-8 px-8 py-6',
          `rounded-b-[${borderRadiusV3[radius]}]`
        )}>
          {footer}
        </div>
      )}
      
      {/* Hover effect overlay for glass variants */}
      {hoverable && isHovered && (variant === 'glass' || variant === 'frosted') && (
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/5 to-purple-500/5 pointer-events-none rounded-inherit" />
      )}
    </div>
  )
})

BirhausCardV3.displayName = 'BirhausCardV3'

// Export hook for managing v3 card state
export function useBirhausCardV3() {
  const [expanded, setExpanded] = useState(false)
  const [selected, setSelected] = useState(false)
  const [bookmarked, setBookmarked] = useState(false)
  const [liked, setLiked] = useState(false)
  const [loading, setLoading] = useState(false)
  
  const toggleExpanded = () => setExpanded(prev => !prev)
  const toggleSelected = () => setSelected(prev => !prev)
  const toggleBookmarked = () => setBookmarked(prev => !prev)
  const toggleLiked = () => setLiked(prev => !prev)
  
  return {
    expanded,
    selected,
    bookmarked,
    liked,
    loading,
    setExpanded,
    setSelected,
    setBookmarked,
    setLiked,
    setLoading,
    toggleExpanded,
    toggleSelected,
    toggleBookmarked,
    toggleLiked,
  }
}

// Helper to create v3 card actions with Spanish-first labels
export function createBirhausCardV3Action(
  id: string,
  labelEs: string,
  onClick: () => void,
  options?: {
    labelEn?: string
    icon?: ReactNode
    variant?: 'primary' | 'secondary' | 'destructive' | 'ghost'
    disabled?: boolean
  }
): BirhausCardV3Action {
  return {
    id,
    label: labelEs,
    labelEs,
    labelEn: options?.labelEn,
    onClick,
    icon: options?.icon,
    variant: options?.variant || 'secondary',
    disabled: options?.disabled || false,
    loading: false
  }
}